/**
 * Logs Screen
 *
 * Visualização de logs de auditoria com descrições humanizadas
 */
import React, { useState } from 'react'
import {
	FileText,
	User,
	Building2,
	Calendar,
	Shield,
	Trash2,
	Edit,
	Plus,
	Eye,
	Download,
	LogIn,
	LogOut,
	Send,
	AlertTriangle,
	Info,
	AlertCircle,
	RefreshCw,
} from 'lucide-react'
import Header from '../organisms/Header'
import DataTable from '../organisms/DataTable'
import FilterBar from '../molecules/FilterBar'
import { Modal } from '../molecules/Modal'
import Card from '../atoms/Card'
import Badge from '../atoms/Badge'
import Button from '../atoms/Button'
import Avatar from '../atoms/Avatar'
import { LoadingScreen } from '../atoms/Spinner'
import { useAuditLogs } from '../../core/admin.hooks'
import { formatDate, exportToCSV } from '../../core/admin.utils'

// Configurações de ações
const ACTION_CONFIG = {
	CREATE: { icon: Plus, color: 'green', label: 'Criou' },
	UPDATE: { icon: Edit, color: 'blue', label: 'Atualizou' },
	DELETE: { icon: Trash2, color: 'red', label: 'Excluiu' },
	VIEW: { icon: Eye, color: 'gray', label: 'Visualizou' },
	LOGIN: { icon: LogIn, color: 'purple', label: 'Fez login' },
	LOGOUT: { icon: LogOut, color: 'yellow', label: 'Fez logout' },
	EXPORT: { icon: Download, color: 'cyan', label: 'Exportou' },
	BROADCAST: { icon: Send, color: 'primary', label: 'Enviou broadcast' },
	RESTORE: { icon: RefreshCw, color: 'green', label: 'Restaurou' },
}

// Configurações de entidades
const ENTITY_CONFIG = {
	USER: { icon: User, label: 'usuário', labelPlural: 'usuários' },
	CLUB: { icon: Building2, label: 'clube', labelPlural: 'clubes' },
	MATCH: { icon: Calendar, label: 'partida', labelPlural: 'partidas' },
	RATING: { icon: FileText, label: 'avaliação', labelPlural: 'avaliações' },
	INVITATION: { icon: FileText, label: 'convite', labelPlural: 'convites' },
	FEEDBACK: { icon: FileText, label: 'feedback', labelPlural: 'feedbacks' },
	TEAM: { icon: FileText, label: 'time', labelPlural: 'times' },
	NOTIFICATION: { icon: FileText, label: 'notificação', labelPlural: 'notificações' },
	BROADCAST: { icon: Send, label: 'broadcast', labelPlural: 'broadcasts' },
	SYSTEM: { icon: Shield, label: 'sistema', labelPlural: 'sistema' },
}

// Configurações de níveis
const LEVEL_CONFIG = {
	INFO: { icon: Info, color: 'blue', label: 'Info' },
	WARN: { icon: AlertTriangle, color: 'yellow', label: 'Aviso' },
	ERROR: { icon: AlertCircle, color: 'red', label: 'Erro' },
	CRITICAL: { icon: AlertCircle, color: 'red', label: 'Crítico' },
}

/**
 * Gera uma descrição humanizada do log
 */
const getLogDescription = (log) => {
	const action = ACTION_CONFIG[log.action] || { label: log.action }
	const entity = ENTITY_CONFIG[log.entity] || { label: log.entity }
	const adminName = log.admin?.name || 'Sistema'

	// Casos especiais
	if (log.action === 'LOGIN') {
		return `${adminName} fez login no painel`
	}
	if (log.action === 'LOGOUT') {
		return `${adminName} saiu do painel`
	}
	if (log.action === 'BROADCAST') {
		const channel = log.changes?.after?.channel
		const channelLabels = { email: 'e-mail', push: 'push', all: 'todos os canais' }
		return `${adminName} enviou broadcast via ${channelLabels[channel] || channel || 'notificação'}`
	}
	if (log.action === 'EXPORT') {
		return `${adminName} exportou dados de ${entity.labelPlural}`
	}

	// Descrição padrão
	let description = `${adminName} ${action.label?.toLowerCase() || 'alterou'} ${entity.label}`

	// Adicionar detalhes se disponíveis
	if (log.changes?.after?.name) {
		description += `: ${log.changes.after.name}`
	} else if (log.changes?.after?.title) {
		description += `: ${log.changes.after.title}`
	} else if (log.changes?.after?.email) {
		description += `: ${log.changes.after.email}`
	}

	return description
}

/**
 * Formata as alterações para exibição
 */
const formatChanges = (changes) => {
	if (!changes || (!changes.before && !changes.after)) return null

	const before = changes.before || {}
	const after = changes.after || {}
	const allKeys = [...new Set([...Object.keys(before), ...Object.keys(after)])]

	// Campos que não devem ser exibidos
	const excludeFields = ['password', 'token', '__v', 'updatedAt', 'createdAt', '_id']

	// Labels amigáveis para campos
	const fieldLabels = {
		name: 'Nome',
		lastname: 'Sobrenome',
		email: 'E-mail',
		phone: 'Telefone',
		whatsapp: 'WhatsApp',
		role: 'Tipo',
		status: 'Status',
		active: 'Ativo',
		avatar: 'Foto',
		bio: 'Bio',
		location: 'Local',
		title: 'Título',
		message: 'Mensagem',
		channel: 'Canal',
		audience: 'Público',
		rating: 'Nota',
		comment: 'Comentário',
	}

	const formattedChanges = allKeys
		.filter(key => !excludeFields.includes(key))
		.filter(key => JSON.stringify(before[key]) !== JSON.stringify(after[key]))
		.map(key => ({
			field: fieldLabels[key] || key,
			before: formatValue(before[key]),
			after: formatValue(after[key]),
		}))

	return formattedChanges.length > 0 ? formattedChanges : null
}

/**
 * Formata um valor para exibição
 */
const formatValue = (value) => {
	if (value === undefined || value === null) return '-'
	if (typeof value === 'boolean') return value ? 'Sim' : 'Não'
	if (typeof value === 'object') {
		if (Array.isArray(value)) return `${value.length} items`
		return JSON.stringify(value)
	}
	if (typeof value === 'string' && value.length > 50) {
		return value.substring(0, 50) + '...'
	}
	return String(value)
}

export default function LogsScreen() {
	const [filters, setFilters] = useState({})
	const [selectedLog, setSelectedLog] = useState(null)
	const [showDetailModal, setShowDetailModal] = useState(false)

	const {
		data,
		loading,
		page,
		setPage,
		setSearch,
	} = useAuditLogs(filters)

	const logs = data?.data || []
	const totalPages = data?.pagination?.totalPages || 1
	const total = data?.pagination?.total || 0

	// Filtros disponíveis
	const filterOptions = [
		{
			key: 'action',
			label: 'Ação',
			type: 'select',
			options: [
				{ value: '', label: 'Todas' },
				{ value: 'CREATE', label: 'Criação' },
				{ value: 'UPDATE', label: 'Atualização' },
				{ value: 'DELETE', label: 'Exclusão' },
				{ value: 'VIEW', label: 'Visualização' },
				{ value: 'LOGIN', label: 'Login' },
				{ value: 'LOGOUT', label: 'Logout' },
				{ value: 'BROADCAST', label: 'Broadcast' },
				{ value: 'EXPORT', label: 'Exportação' },
			],
		},
		{
			key: 'entity',
			label: 'Entidade',
			type: 'select',
			options: [
				{ value: '', label: 'Todas' },
				{ value: 'USER', label: 'Usuário' },
				{ value: 'CLUB', label: 'Clube' },
				{ value: 'MATCH', label: 'Partida' },
				{ value: 'RATING', label: 'Avaliação' },
				{ value: 'FEEDBACK', label: 'Feedback' },
				{ value: 'INVITATION', label: 'Convite' },
				{ value: 'BROADCAST', label: 'Broadcast' },
				{ value: 'SYSTEM', label: 'Sistema' },
			],
		},
		{
			key: 'level',
			label: 'Nível',
			type: 'select',
			options: [
				{ value: '', label: 'Todos' },
				{ value: 'INFO', label: 'Info' },
				{ value: 'WARN', label: 'Aviso' },
				{ value: 'ERROR', label: 'Erro' },
				{ value: 'CRITICAL', label: 'Crítico' },
			],
		},
	]

	// Colunas da tabela
	const columns = [
		{
			key: 'description',
			label: 'Atividade',
			render: (row) => {
				const actionConfig = ACTION_CONFIG[row.action] || { icon: FileText, color: 'gray' }
				const levelConfig = LEVEL_CONFIG[row.level] || LEVEL_CONFIG.INFO
				const Icon = actionConfig.icon
				const description = getLogDescription(row)

				return (
					<div className="flex items-start gap-3">
						<div className={`p-2 bg-${actionConfig.color}-100 dark:bg-${actionConfig.color}-900/30 rounded-lg flex-shrink-0`}>
							<Icon className={`w-4 h-4 text-${actionConfig.color}-600 dark:text-${actionConfig.color}-400`} />
						</div>
						<div className="min-w-0 flex-1">
							<p className="text-text text-sm leading-relaxed">
								{description}
							</p>
							<div className="flex flex-wrap items-center gap-2 mt-1">
								<span className="text-xs text-text-muted">
									{formatDate(row.createdAt, "dd/MM/yyyy 'às' HH:mm")}
								</span>
								{row.level !== 'INFO' && (
									<Badge variant={levelConfig.color} size="xs">
										{levelConfig.label}
									</Badge>
								)}
							</div>
						</div>
					</div>
				)
			},
		},
		{
			key: 'admin',
			label: 'Administrador',
			render: (row) => (
				<div className="flex items-center gap-2">
					<Avatar
						src={row.admin?.avatar}
						name={row.admin?.name || 'Sistema'}
						size="sm"
					/>
					<div className="min-w-0">
						<p className="text-sm text-text truncate">
							{row.admin?.name || 'Sistema'}
						</p>
					</div>
				</div>
			),
		},
		{
			key: 'entity',
			label: 'Tipo',
			render: (row) => {
				const entityConfig = ENTITY_CONFIG[row.entity] || { icon: FileText, label: row.entity }
				const actionConfig = ACTION_CONFIG[row.action] || { color: 'gray' }
				return (
					<Badge variant={actionConfig.color} size="sm">
						{entityConfig.label}
					</Badge>
				)
			},
		},
	]

	// Ações do menu
	const getRowActions = (row) => [
		{
			icon: Eye,
			label: 'Ver detalhes',
			onClick: () => {
				setSelectedLog(row)
				setShowDetailModal(true)
			},
		},
	]

	// Exportar para CSV
	const handleExport = () => {
		const exportData = logs.map((log) => ({
			Data: formatDate(log.createdAt, 'dd/MM/yyyy HH:mm:ss'),
			Atividade: getLogDescription(log),
			Ação: ACTION_CONFIG[log.action]?.label || log.action,
			Entidade: ENTITY_CONFIG[log.entity]?.label || log.entity,
			Administrador: log.admin?.name || 'Sistema',
			Email: log.admin?.email || '-',
			Nível: LEVEL_CONFIG[log.level]?.label || log.level,
			IP: log.metadata?.ip || '-',
		}))
		exportToCSV(exportData, `logs_${formatDate(new Date(), 'yyyy-MM-dd')}`)
	}

	if (loading && !data) {
		return (
			<div className="min-h-screen">
				<Header title="Logs" />
				<LoadingScreen />
			</div>
		)
	}

	return (
		<div className="min-h-screen">
			<Header
				title="Logs de Auditoria"
				subtitle={`${total} registros`}
				actions={
					<Button
						variant="outline"
						size="sm"
						onClick={handleExport}
						disabled={logs.length === 0}
					>
						<Download className="w-4 h-4 mr-2" />
						<span className="hidden sm:inline">Exportar CSV</span>
						<span className="sm:hidden">CSV</span>
					</Button>
				}
			/>

			<div className="p-3 md:p-6 space-y-3 md:space-y-4">
				{/* Filtros */}
				<FilterBar
					filters={filterOptions}
					values={filters}
					onChange={setFilters}
					onSearch={setSearch}
					searchPlaceholder="Buscar por usuário, ação..."
				/>

				{/* Tabela */}
				<DataTable
					columns={columns}
					data={logs}
					loading={loading}
					emptyMessage="Nenhum log encontrado"
					page={page}
					totalPages={totalPages}
					onPageChange={setPage}
					actions={getRowActions}
					mobileColumns={['description']}
				/>
			</div>

			{/* Modal de detalhes */}
			<Modal
				isOpen={showDetailModal}
				onClose={() => setShowDetailModal(false)}
				title="Detalhes da Atividade"
				size="lg"
			>
				{selectedLog && (
					<div className="space-y-4">
						{/* Descrição principal */}
						<Card className="p-3 md:p-4 bg-base-400">
							<div className="flex items-start gap-3">
								{(() => {
									const actionConfig = ACTION_CONFIG[selectedLog.action] || { icon: FileText, color: 'gray' }
									const Icon = actionConfig.icon
									return (
										<div className={`p-2 bg-${actionConfig.color}-100 dark:bg-${actionConfig.color}-900/30 rounded-lg flex-shrink-0`}>
											<Icon className={`w-5 h-5 text-${actionConfig.color}-600 dark:text-${actionConfig.color}-400`} />
										</div>
									)
								})()}
								<div>
									<p className="text-text font-medium">
										{getLogDescription(selectedLog)}
									</p>
									<p className="text-sm text-text-muted mt-1">
										{formatDate(selectedLog.createdAt, "EEEE, dd 'de' MMMM 'de' yyyy 'às' HH:mm:ss")}
									</p>
								</div>
							</div>
						</Card>

						{/* Informações do admin */}
						<div>
							<p className="text-xs md:text-sm text-text-muted mb-2 font-medium">Administrador</p>
							<div className="flex items-center gap-3 p-3 bg-base-400 rounded-lg">
								<Avatar
									src={selectedLog.admin?.avatar}
									name={selectedLog.admin?.name || 'Sistema'}
									size="md"
								/>
								<div className="min-w-0 flex-1">
									<p className="font-medium text-text">
										{selectedLog.admin?.name} {selectedLog.admin?.lastname || ''}
									</p>
									<p className="text-sm text-text-body truncate">
										{selectedLog.admin?.email || '-'}
									</p>
								</div>
							</div>
						</div>

						{/* Grid de informações */}
						<div className="grid grid-cols-2 gap-3 md:gap-4">
							<InfoItem
								label="Ação"
								value={ACTION_CONFIG[selectedLog.action]?.label || selectedLog.action}
							/>
							<InfoItem
								label="Entidade"
								value={ENTITY_CONFIG[selectedLog.entity]?.label || selectedLog.entity}
							/>
							<InfoItem
								label="Nível"
								value={LEVEL_CONFIG[selectedLog.level]?.label || selectedLog.level}
							/>
							<InfoItem
								label="IP"
								value={selectedLog.metadata?.ip || '-'}
								mono
							/>
						</div>

						{/* Alterações formatadas */}
						{(() => {
							const changes = formatChanges(selectedLog.changes)
							if (!changes) return null

							return (
								<div>
									<p className="text-xs md:text-sm text-text-muted mb-2 font-medium">Alterações</p>
									<Card className="bg-base-400 overflow-hidden">
										<div className="divide-y divide-base-200">
											{changes.map((change, idx) => (
												<div key={idx} className="p-3 flex flex-col sm:flex-row sm:items-center gap-1 sm:gap-3">
													<span className="text-sm font-medium text-text min-w-[100px]">
														{change.field}
													</span>
													<div className="flex items-center gap-2 text-sm flex-1 min-w-0">
														{change.before !== '-' && (
															<>
																<span className="text-text-muted line-through truncate max-w-[120px]">
																	{change.before}
																</span>
																<span className="text-text-muted">→</span>
															</>
														)}
														<span className="text-text truncate">
															{change.after}
														</span>
													</div>
												</div>
											))}
										</div>
									</Card>
								</div>
							)
						})()}

						{/* Mensagem adicional */}
						{selectedLog.message && (
							<div>
								<p className="text-xs md:text-sm text-text-muted mb-2 font-medium">Mensagem</p>
								<Card className="p-3 bg-base-400">
									<p className="text-sm text-text">{selectedLog.message}</p>
								</Card>
							</div>
						)}

						{/* Metadados técnicos (colapsável) */}
						<details className="group">
							<summary className="text-xs text-text-muted cursor-pointer hover:text-text flex items-center gap-1">
								<span>Dados técnicos</span>
								<span className="group-open:rotate-90 transition-transform">▶</span>
							</summary>
							<Card className="mt-2 p-3 bg-base-400">
								<div className="grid grid-cols-1 gap-2 text-xs font-mono">
									<div>
										<span className="text-text-muted">ID do Log: </span>
										<span className="text-text break-all">{selectedLog._id}</span>
									</div>
									{selectedLog.entityId && (
										<div>
											<span className="text-text-muted">ID da Entidade: </span>
											<span className="text-text break-all">{selectedLog.entityId}</span>
										</div>
									)}
									{selectedLog.metadata?.userAgent && (
										<div>
											<span className="text-text-muted">User Agent: </span>
											<span className="text-text break-all">{selectedLog.metadata.userAgent}</span>
										</div>
									)}
									{selectedLog.metadata?.endpoint && (
										<div>
											<span className="text-text-muted">Endpoint: </span>
											<span className="text-text">{selectedLog.metadata.method} {selectedLog.metadata.endpoint}</span>
										</div>
									)}
								</div>
							</Card>
						</details>
					</div>
				)}
			</Modal>
		</div>
	)
}

// Componente auxiliar
function InfoItem({ label, value, mono = false }) {
	return (
		<div className="p-2 md:p-3 bg-base-400 rounded-lg">
			<p className="text-xs text-text-muted mb-0.5">{label}</p>
			<p className={`text-sm font-medium text-text ${mono ? 'font-mono' : ''}`}>
				{value}
			</p>
		</div>
	)
}
